import React, { useState } from "react";
import {
    Box,
    Paper,
    Typography,
    Card,
    CardContent,
    CardHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    List,
    ListItem,
    ListItemText,
    Divider,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Chip
} from "@mui/material";
import { Icon } from "@iconify/react";
import moment from "moment";

const SSLDetails = ({ data }) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [expandedSection, setExpandedSection] = useState(null);

    const handleOpenDialog = (section) => {
        setExpandedSection(section);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setExpandedSection(null);
    };

    const renderValue = (value) => {
        //console.log('render method-==-', value);
        if (value === null || value === undefined) {
            return <div></div>;
        } else if (typeof value === 'object' && !Array.isArray(value)) {
            return (
                <table style={{ width: '100%', marginBottom: '15px' }} border="1">
                    <tbody>

                        {Object.keys(value).filter((item) => (item !== 'companyId' && item !== 'sosKey' && item !== '_id' && item !== 'companyID')).map((key, index) => {
                            return (<>
                                <tr><td style={{ padding: '0.5rem' }}><b style={{ textTransform: 'capitalize' }}>{key}:</b></td> <td style={{ padding: '0.5rem' }}>
                                    {typeof value[key] === 'object' ? renderValue(value[key]) : value[key]}</td></tr>

                            </>)
                        })}
                    </tbody>
                </table>
            );
        } else if (Array.isArray(value)) {
            return (
                <ul style={{ listStyle: 'none' }}>
                    {value.map((item, index) => (
                        <li key={index}>
                            {typeof item === 'object' ? renderValue(item) : item}
                        </li>
                    ))}
                </ul>
            );
        } else {
            return <div>{value}</div>;
        }
    };

    return (
        <Paper elevation={3} sx={{ padding: 3, width: "100%", maxWidth: 900, margin: "0 auto" }}>
            {/* 📌 Title */}
            <Typography variant="h4" fontWeight="bold" mb={3} color="rgb(0, 145, 70)" sx={{ textAlign: "center" }}>
                SSL Scan Report
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                    <Icon icon="mdi:web" width={24} height={24} style={{ marginRight: 8 }} />
                    Target:
                </Typography>
                <Chip label={data?.result?.ServerInformation?.hostname} color="primary" variant="outlined" sx={{ fontSize: "1rem", padding: "5px" }} />
            </Box>

            <Typography variant="body1" sx={{ color: "#777", mb: 2 }}>
                Report Date: {moment(data?.date).format("MMM DD, YYYY HH:mm")}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
                {/* 📌 Server Information */}
                <Grid item xs={12}>
                    <Card>
                        <CardHeader
                            title="Server Information"
                            avatar={<Icon icon="mdi:server" width={24} height={24} />}
                        />
                        <CardContent>
                            <List>
                                {Object.entries(data?.result?.ServerInformation || {}).map(([key, value]) => (
                                    <ListItem key={key}>
                                        <ListItemText primary={key.replace(/([A-Z])/g, " $1").trim()} secondary={value} />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 📌 Certificate Analysis */}
                <Grid item xs={12}>
                    <Card>
                        <CardHeader
                            title="Certificate Analysis"
                            avatar={<Icon icon="mdi:certificate" width={24} height={24} />}
                        />
                        <CardContent>
                            <List>
                                <ListItem>
                                    <ListItemText primary="Common Name" secondary={data?.result?.CertificateAnalysis?.commonName} />
                                </ListItem>

                                {/* ✅ Handle Nested Object for Issuer */}
                                <ListItem>
                                    <ListItemText
                                        primary="Issuer"
                                        secondary={
                                            data?.result?.CertificateAnalysis?.issuer
                                                ? `${data.result.CertificateAnalysis.issuer.organization}, ${data.result.CertificateAnalysis.issuer.country} (${data.result.CertificateAnalysis.issuer.name})`
                                                : "N/A"
                                        }
                                    />
                                </ListItem>

                                <ListItem>
                                    <ListItemText
                                        primary="Validity Period"
                                        secondary={`From: ${moment(data?.result?.CertificateAnalysis?.validityPeriod?.from).format("MMM DD, YYYY")}  -  To: ${moment(data?.result?.CertificateAnalysis?.validityPeriod?.to).format("MMM DD, YYYY")}`}
                                    />
                                </ListItem>

                                <ListItem>
                                    <ListItemText
                                        primary="Key Info"
                                        secondary={`${data?.result?.CertificateAnalysis?.keyInfo?.type} (${data?.result?.CertificateAnalysis?.keyInfo?.size} bits)`}
                                    />
                                </ListItem>

                                <ListItem>
                                    <ListItemText
                                        primary="Subject Alternative Names"
                                        secondary={(data?.result?.CertificateAnalysis?.subjectAlternativeNames || []).join(", ")}
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid>


                {/* 📌 Certificate Chain Validation */}
                <Grid item xs={12}>
                    <Card>
                        <CardHeader
                            title="Certificate Chain Validation"
                            avatar={<Icon icon="mdi:certificate-outline" width={24} height={24} />}
                        />
                        <CardContent>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Name</strong></TableCell>
                                            <TableCell><strong>Organization</strong></TableCell>
                                            <TableCell><strong>Valid From</strong></TableCell>
                                            <TableCell><strong>Valid To</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data?.result?.CertificateChainValidation?.certificateChain.map((cert, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{cert.name}</TableCell>
                                                <TableCell>{cert.organization}</TableCell>
                                                <TableCell>{moment(cert.validityPeriod.from).format("MMM DD, YYYY")}</TableCell>
                                                <TableCell>{moment(cert.validityPeriod.to).format("MMM DD, YYYY")}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 📌 Supported Ciphers & Protocols */}
                <Grid item xs={12}>
                    <Card>
                        <CardHeader
                            title="Supported Ciphers & Protocols"
                            avatar={<Icon icon="mdi:security" width={24} height={24} />}
                        />
                        <CardContent>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Protocol</strong></TableCell>
                                            <TableCell><strong>Supported</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(data?.result?.SupportedCiphersAndProtocols || {}).map(([key, value], index) => {
                                            const supported = value?.supported; // ✅ safe (works even if value is null)

                                            return (
                                                <TableRow key={index}>
                                                    <TableCell>{key}</TableCell>
                                                    <TableCell>
                                                        {supported === true ? "Yes ✅" : supported === false ? "No ❌" : "-"}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                        </CardContent>
                    </Card>
                </Grid>

                {/* 📌 Vulnerability Assessments */}
                <Grid item xs={12}>
                    <Card>
                        <CardHeader
                            title="Vulnerability Assessments"
                            avatar={<Icon icon="mdi:shield-check" width={24} height={24} />}
                        />
                        <CardContent>
                            <List>
                                {Object.entries(data?.result?.VulnerabilityAssessments || {}).map(([key, value]) => (
                                    <ListItem key={key}>
                                        <ListItemText primary={key.replace(/([A-Z])/g, " $1").trim()} secondary={value ? "✅ Safe" : "⚠️ Vulnerable"} />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 📌 Key Exchange */}
                <Grid item xs={12}>
                    <Card>
                        <CardHeader
                            title="Key Exchange"
                            avatar={<Icon icon="mdi:key" width={24} height={24} />}
                        />
                        <CardContent>
                            <List>
                                <ListItem>
                                    <ListItemText primary="ECDH Supported" secondary={data?.result?.KeyExchange?.ecdhSupported ? "Yes ✅" : "No ❌"} />
                                </ListItem>
                                <ListItem>
                                    <ListItemText primary="Supported Curves" secondary={data?.result?.KeyExchange?.supportedCurves.map(c => c.name).join(", ")} />
                                </ListItem>
                                <ListItem>
                                    <ListItemText primary="Rejected Curves" secondary={data?.result?.KeyExchange?.rejectedCurves.map(c => c.name).join(", ")} />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* 📌 Dialog for Show More */}
            {/*  <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
                <DialogTitle>{expandedSection}</DialogTitle>
                <DialogContent dividers>
                    {renderValue(data?.result[expandedSection])}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="error">Close</Button>
                </DialogActions>
            </Dialog> */}
        </Paper>
    );
};

export default SSLDetails;
